// Follow-up no negócio (05/10/2026): onde ele está no ciclo, como é feito
// (desligado / tarefa / e-mail automático), "Cliente respondeu" e os e-mails e
// tarefas que o follow-up já fez. Só aparece em negócio na etapa Follow-up ou
// que já teve follow-up. Regra e fila completas: Comercial › Follow-ups.
import { useCallback, useEffect, useState } from 'react';
import { App as AntApp, Button, Card, Collapse, DatePicker, Select, Space, Tag, Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { usePerms } from '../../hooks/usePerms';
import { fu, erroDe, type DoNegocio, type Modo } from '../comercial-followups/api';
import { COR_SITUACAO, ROTULO_MODO } from '../comercial-followups/regras';
import { ModalRespondeu } from '../comercial-followups/AbaFila';
import { TabelaEnvios } from '../comercial-followups/AbaEnviados';

const { Text } = Typography;

export function FollowupBloco({ negocio, reloadFlag, onMudou }: {
  negocio: { id: number; titulo: string; status: string; origem?: string | null };
  reloadFlag?: number;
  onMudou?: () => void;
}) {
  const { message } = AntApp.useApp();
  const { pode } = usePerms();
  const podeOperar = pode('comercial.pipeline.editar_etapa_funil') || pode('admin.permissoes.editar');
  const navigate = useNavigate();
  const [d, setD] = useState<DoNegocio | null>(null);
  const [mexendo, setMexendo] = useState(false);
  const [respondendo, setRespondendo] = useState(false);

  const carregar = useCallback(async () => {
    if (negocio.origem === 'licitacao') return;
    try { setD(await fu.negocio(negocio.id)); } catch { setD(null); /* tabela ausente ou sem acesso: o bloco some */ }
  }, [negocio.id, negocio.origem]);
  useEffect(() => { carregar(); }, [carregar, reloadFlag, negocio.status]);

  if (!d || (!d.em_followup && !d.envios.length)) return null;

  const definir = async (mudanca: { modo?: Modo; pausado_ate?: string | null }) => {
    setMexendo(true);
    try { setD(await fu.definir(negocio.id, mudanca)); onMudou?.(); }
    catch (e) { message.error(erroDe(e)); }
    finally { setMexendo(false); }
  };

  const e = d.estado;
  return (
    <Card size="small" style={{ marginBottom: 12 }}
      title={<Space wrap size={6}>
        <span>🔁 Follow-up</span>
        <Tag color={COR_SITUACAO[d.situacao.codigo] || 'default'}>{d.situacao.rotulo}</Tag>
        {d.em_followup && e && e.modo !== 'desligado' && <Text type="secondary" style={{ fontSize: 12 }}>{e.tentativas}/{d.max_tentativas} feitos</Text>}
      </Space>}
      extra={<Button size="small" type="link" onClick={() => navigate('/comercial/followups')}>Fila e regras</Button>}>
      {d.em_followup && (
        <Space wrap size={8} style={{ marginBottom: d.envios.length ? 8 : 0 }}>
          <Select size="small" style={{ width: 190 }} value={e?.modo} placeholder="Desligado" disabled={!podeOperar || mexendo} loading={mexendo}
            options={(['desligado', 'manual', 'automatico'] as Modo[]).map(m => ({ value: m, label: ROTULO_MODO[m] }))}
            onChange={(m: Modo) => definir({ modo: m })} />
          {e?.modo === 'automatico' && !d.envio_automatico_ativo && (
            <Tooltip title="O e-mail automático está desligado na regra — por enquanto vira tarefa."><Tag color="gold">envio desligado na regra</Tag></Tooltip>
          )}
          {e?.modo === 'automatico' && !d.contato_email && <Tag color="gold">contato sem e-mail — vira tarefa</Tag>}
          {e?.modo === 'automatico' && !d.remetente && <Tag color="gold">responsável sem e-mail — vira tarefa</Tag>}
          {e?.modo === 'automatico' && d.remetente && d.contato_email && (
            <Text type="secondary" style={{ fontSize: 12 }}>de {d.remetente.email} para {d.contato_email}</Text>
          )}
          {podeOperar && e && e.modo !== 'desligado' && (
            <>
              <Button size="small" onClick={() => setRespondendo(true)}>Cliente respondeu</Button>
              {d.situacao.codigo === 'pausado'
                ? <Button size="small" onClick={() => definir({ pausado_ate: null })}>Retomar</Button>
                : <DatePicker size="small" placeholder="Pausar até" format="DD/MM/YYYY" style={{ width: 130 }}
                    disabledDate={x => x.isBefore(dayjs(), 'day')}
                    onChange={x => x && definir({ pausado_ate: x.format('YYYY-MM-DD') })} />}
            </>
          )}
        </Space>
      )}
      {d.envios.length > 0 && (
        <Collapse size="small" ghost items={[{
          key: 'h', label: `Histórico do follow-up (${d.envios.length})`,
          children: <TabelaEnvios itens={d.envios} comNegocio={false} />,
        }]} />
      )}
      {respondendo && (
        <ModalRespondeu item={negocio} onCancelar={() => setRespondendo(false)}
          onConfirmar={async (resumo, pausa) => {
            try { setD(await fu.respondeu(negocio.id, resumo, pausa)); setRespondendo(false); message.success('Registrado. O ciclo recomeça a partir de hoje.'); onMudou?.(); }
            catch (err) { message.error(erroDe(err)); }
          }} />
      )}
    </Card>
  );
}
